import React from "react";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "../contexts/ContextProvider";
import Permission from "../components/Util/Permission";
import { PRIMARY, SHELL_THEME as THEME } from "../shellTokens";

const CARDS = [
  {
    key: "company",
    title: "Company Settings",
    icon: "⌂",
    link: "/settings/companySettings",
    description: "Name, logo, address and contact details — what every invoice prints.",
    permission: "COMPANY_SETTINGS",
    tint: "#38bdf8",
  },
  {
    key: "roles",
    title: "Access — Roles",
    icon: "▦",
    link: "/settings/roleSettings",
    description: "What each role can do, and who is in it.",
    permission: "ROLE_SETTINGS",
    tint: PRIMARY,
  },
  {
    key: "marketplace",
    title: "Marketplace Settings",
    icon: "◈",
    link: "/settings/marketplaceSettings",
    description: "Manage connected marketplaces and business page configurations.",
    permission: "MARKETPLACE_SETTINGS",
    tint: "#10b981",
  },
  {
    key: "product",
    title: "Product Settings",
    icon: "◐",
    link: "/settings/productSettings",
    description: "Customize product attributes, categories, and inventory rules.",
    permission: "PRODUCT_SETTINGS",
    tint: "#f59e0b",
  },
  {
    key: "team",
    title: "Team Settings",
    icon: "✦",
    link: "/settings/teamSettings",
    description: "Group staff into teams; orders and dashboards are separated by team.",
    permission: "TEAM_SETTINGS",
    tint: "#ec4899",
  },
];

const Settings = () => {
  const navigate = useNavigate();
  const { darkMode } = useStateContext();

  const rootStyle = {
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? "dark" : "light"]).map(([k, v]) => [`--${k}`, v])
    ),
    "--primary": PRIMARY,
  };

  return (
    <div className="et-settings" style={rootStyle}>
      <style>{`
        .et-settings{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:20px}
        .et-settings__card{cursor:pointer;transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease}
        .et-settings__card:hover{transform:translateY(-2px);border-color:var(--primary);box-shadow:0 0 0 2px var(--primary),0 0 20px ${PRIMARY}40,0 8px 24px rgba(0,0,0,.18)}.et-settings__card-arrow{color:var(--dim)}.et-settings__card:hover .et-settings__card-icon{background:${PRIMARY}33;color:var(--primary)}.et-settings__card:hover .et-settings__card-arrow{color:var(--primary)}
      `}</style>

      {/* Header */}
      <div>
        <div style={{ font: "700 22px Outfit, sans-serif", letterSpacing: "-.01em" }}>
          Settings
        </div>
        <div style={{ font: "400 13px Outfit, sans-serif", color: "var(--muted)", marginTop: 2 }}>
          Manage your application configuration and preferences
        </div>
      </div>

      {/* Card grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 14,
        }}
      >
        {CARDS.map((item) => (
          <Permission required={item.permission} key={item.key}>
            <div
              className="et-settings__card"
              onClick={() => navigate(item.link)}
              style={{
                background: "var(--surface)",
                border: "2px solid var(--border)",
                borderRadius: 14,
                padding: "20px 18px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                position: "relative",
              }}
            >
              {/* Icon */}
              <span
                className="et-settings__card-icon"
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: `${item.tint}24`,
                  color: item.tint,
                  fontSize: 18,
                  transition: "background .18s ease, color .18s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </span>

              {/* Content */}
              <div>
                <div
                  style={{
                    font: "600 15px Outfit, sans-serif",
                    color: "var(--text)",
                    marginBottom: 4,
                  }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    font: "400 12.5px/1.5 Outfit, sans-serif",
                    color: "var(--muted)",
                  }}
                >
                  {item.description}
                </div>
              </div>

              {/* Arrow indicator */}
              <div
                className="et-settings__card-arrow"
                style={{
                  position: "absolute",
                  top: 20,
                  right: 18,
                  fontSize: 18,
                  fontWeight: 700,
                  transition: "color .18s ease",
                }}
              >
                →
              </div>
            </div>
          </Permission>
        ))}
      </div>
    </div>
  );
};

export default Settings;
